import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthSession } from "@/lib/auth-client";
import { Logo } from "@/components/Logo";

const features = [
  {
    title: "Track Rent Payments",
    description:
      "Automatically generate monthly rent records and track payment status for every property.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
      </svg>
    ),
  },
  {
    title: "Expense Management",
    description:
      "Log maintenance, tax, insurance, and utility expenses per property in one place.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
  },
  {
    title: "Agreement Alerts",
    description:
      "Get notified before rental agreements expire so you never miss a renewal.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
      </svg>
    ),
  },
  {
    title: "Tenant Verification",
    description:
      "Let tenants confirm payments with proof uploads. Approve or reject from your dashboard.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
      </svg>
    ),
  },
];

export function LandingPage() {
  const navigate = useNavigate();
  const { session, isPending } = useAuthSession();

  useEffect(() => {
    if (isPending || !session?.user) return;
    navigate("/dashboard", { replace: true });
  }, [session, isPending, navigate]);

  if (session?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400" />
          <p className="text-sm text-slate-400 dark:text-slate-500">Redirecting to dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Logo size="md" />
          <nav className="flex items-center gap-2">
            <Link
              to="/login"
              className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Log in
            </Link>
            <Link
              to="/register"
              className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 transition-all"
            >
              Get Started
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[600px] w-[800px] rounded-full bg-primary-100/40 dark:bg-primary-900/30 blur-3xl" />
          </div>
          <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 dark:bg-primary-900/40 px-4 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-300 mb-8 ring-1 ring-primary-100 dark:ring-primary-800">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500 dark:bg-primary-400 animate-pulse" />
              Built for Indian Landlords
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-5xl lg:text-6xl leading-[1.1]">
              Rent management
              <br />
              <span className="bg-gradient-to-r from-primary-600 to-primary-500 dark:from-primary-400 dark:to-primary-300 bg-clip-text text-transparent">
                made simple.
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-500 dark:text-slate-400 leading-relaxed">
              Stop tracking rent on spreadsheets. RentMaadi gives small landlords
              a clean dashboard to manage properties, track payments, log expenses,
              and stay on top of agreements.
            </p>
            <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to="/register"
                className="w-full sm:w-auto rounded-2xl bg-primary-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary-600/25 hover:bg-primary-700 hover:shadow-xl hover:shadow-primary-600/30 active:scale-[0.98] transition-all"
              >
                Start Free — No Card Needed
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-8 py-3.5 text-base font-semibold text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500 active:scale-[0.98] transition-all"
              >
                I already have an account
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 sm:text-4xl">
                Everything you need, nothing you don&apos;t
              </h2>
              <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">
                Designed for landlords with 1 to 10 properties. No complexity, just clarity.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="group rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 p-6 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none hover:border-primary-200 dark:hover:border-primary-700 transition-all"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 group-hover:bg-primary-100 dark:group-hover:bg-primary-800/50 transition-colors">
                    {f.icon}
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {f.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing teaser */}
        <section className="py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="relative overflow-hidden rounded-3xl bg-slate-900 dark:bg-slate-800 p-8 sm:p-14 text-center border border-slate-800 dark:border-slate-700">
              <div className="absolute inset-0 -z-0">
                <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary-600/20 dark:bg-primary-500/20 blur-3xl" />
                <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-primary-400/10 dark:bg-primary-400/15 blur-3xl" />
              </div>
              <div className="relative z-10">
                <h2 className="text-3xl font-bold text-white sm:text-4xl">
                  Free to start. Pro when you&apos;re ready.
                </h2>
                <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">
                  Manage up to 3 properties free forever. Upgrade to Pro for
                  tenant portal, reports, and email notifications.
                </p>
                <Link
                  to="/register"
                  className="mt-8 inline-block rounded-2xl bg-primary-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary-600/25 hover:bg-primary-500 active:scale-[0.98] transition-all"
                >
                  Create Your Free Account
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 py-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            RentMaadi does not process payments. It only records and organizes
            rental information provided by users.
          </p>
          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
            &copy; {new Date().getFullYear()} RentMaadi. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
